package com.schoolconnect.app.feature.exams

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Surface
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.schoolconnect.app.designsystem.*
import com.schoolconnect.app.designsystem.list.AdminListHorizontalScrollbar
import com.schoolconnect.app.designsystem.list.AdminListSearchField
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.ui.platform.LocalDensity
import ischoolconnect.sharedui.generated.resources.*
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerType
import kotlinx.coroutines.launch
import kotlin.math.round

private data class ResultStudent(val studentId: String, val roll: String, val name: String, val grade: String, val percent: String, val total: String, val rank: String, val subjectValues: Map<String, String> = emptyMap(), val notes: String = "")
private data class ValidationIssue(val roll: String, val student: String, val subject: String, val issue: String)
private data class ReviewValidation(
    val totalStudents: Int,
    val enteredStudents: Int,
    val issues: List<ValidationIssue>
) {
    val pendingStudents: Int get() = (totalStudents - enteredStudents).coerceAtLeast(0)
    val completion: Double get() = if (totalStudents == 0) 0.0 else (enteredStudents.toDouble() / totalStudents * 100.0).roundResult()
}

private fun ExamResultStudentUi.toResultStudent() = ResultStudent(
    studentId = studentId,
    roll = roll,
    name = name,
    grade = grade,
    percent = percent,
    total = total,
    rank = rank,
    subjectValues = subjectValues,
    notes = notes
)

private val gradeOptions = listOf("A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D", "AB")
private val resultRowHeight = 62.dp
// Material 3's single-line outlined field needs at least 56.dp to keep the
// text and cursor inside the field. A smaller fixed height clips the glyphs
// vertically on desktop and in the browser.
private val resultFieldHeight = 56.dp

private fun Double.toDisplayNumber(): String = if (this % 1.0 == 0.0) toInt().toString() else toString()

private fun String.toResultParts(): List<String> = split("/").map(String::trim).let { parts ->
    parts + List((3 - parts.size).coerceAtLeast(0)) { "" }
}

private data class CalculatedStudentResult(val total: Double, val percentage: Double)

private fun Double.roundResult(): Double = round(this * 100.0) / 100.0

private fun String.isResultValueEntered(): Boolean = trim().isNotBlank()

private fun String.isAbsentResult(): Boolean = trim().equals("AB", ignoreCase = true)

private fun ResultStudent.reviewValidation(
    row: ExamRowUi,
    subjectConfigs: List<Pair<String, ExamSubjectSelectionUi>>,
    subjectOptions: Map<String, String>
): List<ValidationIssue> {
    val issues = buildList {
        if (row.hasOverallGrade && !grade.isResultValueEntered()) {
            add(ValidationIssue(roll, name, "Overall Result", "Grade is required"))
        }
        if (row.hasOverallRank && !rank.isResultValueEntered()) {
            add(ValidationIssue(roll, name, "Overall Result", "Rank is required"))
        }
        subjectConfigs.forEach { (subjectId, config) ->
            val parts = subjectValues[subjectId].orEmpty().toResultParts()
            if (parts.any(String::isAbsentResult)) return@forEach
            val subject = subjectOptions[subjectId].orEmpty().ifBlank { "Subject name unavailable" }
            when {
                config.hasMarks && !parts.getOrNull(1).orEmpty().isResultValueEntered() ->
                    add(ValidationIssue(roll, name, subject, "Marks is required"))
                config.hasGrade && !parts.getOrNull(0).orEmpty().isResultValueEntered() ->
                    add(ValidationIssue(roll, name, subject, "Grade is required"))
                config.hasRank && !parts.getOrNull(2).orEmpty().isResultValueEntered() ->
                    add(ValidationIssue(roll, name, subject, "Rank is required"))
            }
        }
    }
    return issues
}

private fun buildReviewValidation(
    students: List<ResultStudent>,
    row: ExamRowUi,
    subjectConfigs: List<Pair<String, ExamSubjectSelectionUi>>,
    subjectOptions: Map<String, String>
): ReviewValidation {
    val issues = students.flatMap { it.reviewValidation(row, subjectConfigs, subjectOptions) }
    val studentsWithIssues = issues.map { it.roll }.toSet()
    return ReviewValidation(
        totalStudents = students.size,
        enteredStudents = (students.map { it.roll }.toSet() - studentsWithIssues).size,
        issues = issues
    )
}

private fun ResultStudent.calculate(subjectConfigs: List<Pair<String, ExamSubjectSelectionUi>>): CalculatedStudentResult {
    var total = 0.0
    var maximum = 0.0
    subjectConfigs.forEach { (subjectId, config) ->
        if (!config.hasMarks) return@forEach
        val parts = subjectValues[subjectId].orEmpty().toResultParts()
        if (parts.any { it.equals("AB", ignoreCase = true) }) return@forEach
        maximum += config.configuredTotal() ?: 0.0
        total += parts.getOrNull(1)?.toDoubleOrNull() ?: 0.0
    }
    return CalculatedStudentResult(
        total = total.roundResult(),
        percentage = if (maximum > 0.0) (total / maximum * 100.0).roundResult() else 0.0
    )
}

private fun ResultStudent.toDraft(row: ExamRowUi, subjectConfigs: List<Pair<String, ExamSubjectSelectionUi>>): ExamResultEntryDraft {
    val calculated = calculate(subjectConfigs)
    return ExamResultEntryDraft(
        studentId = studentId,
        rollNumber = roll.trim().takeIf(String::isNotBlank),
        name = name.trim().takeIf(String::isNotBlank),
        total = calculated.total,
        totalPercentage = calculated.percentage,
        grade = grade.trim().takeIf { row.hasOverallGrade && it.isNotBlank() },
        rank = rank.trim().toIntOrNull().takeIf { row.hasOverallRank },
        notes = notes.trim().takeIf(String::isNotBlank),
        subjectMarks = subjectConfigs.map { (subjectId, config) ->
            val parts = subjectValues[subjectId].orEmpty().toResultParts()
            val absent = parts.any { it.equals("AB", ignoreCase = true) }
            val enteredTotal = parts.getOrNull(1)?.toDoubleOrNull() ?: 0.0
            ExamSubjectMarkDraft(
                subjectId = subjectId,
                internal = 0.0,
                main = enteredTotal,
                total = enteredTotal,
                // AB is the transport marker for an absent subject even when
                // this exam does not display a Grade column.
                grade = if (absent) "AB" else parts.getOrNull(0)?.takeIf { config.hasGrade && it.isNotBlank() },
                rank = parts.getOrNull(2)?.toIntOrNull().takeIf { config.hasRank }
            )
        }
    )
}

@Composable
internal fun ExamResultsEntryDialog(row: ExamRowUi?, subjectOptions: Map<String, String> = emptyMap(), subjectCodes: Map<String, String> = emptyMap(), apiStudents: List<ExamResultStudentUi> = emptyList(), errorMessage: String = "", subjectNameError: String = "", saveErrorMessage: String = "", isLoading: Boolean = false, isSaving: Boolean = false, isLoadingImportExport: Boolean = false, importExportError: String = "", studentCount: Int = 0, onDismiss: () -> Unit, onDownloadTemplate: (String) -> Unit = {}, onImportResults: (String, ByteArray) -> Unit = { _, _ -> }, onSave: (String, List<ExamResultEntryDraft>) -> Unit = { _, _ -> }) {
    if (row == null) return
    val pickerScope = rememberCoroutineScope()
    val importPicker = rememberFilePickerLauncher(type = PickerType.File(listOf("xlsx")), title = "Select Exam Results Workbook") { file ->
        file ?: return@rememberFilePickerLauncher
        pickerScope.launch { runCatching { file.readBytes() }.onSuccess { bytes -> onImportResults(file.name, bytes) } }
    }
    var tab by remember(row.id) { mutableStateOf(0) }
    var search by remember(row.id) { mutableStateOf("") }
    val apiRows = remember(apiStudents) { apiStudents.map { it.toResultStudent() } }
    var students by remember(row.id, apiRows) { mutableStateOf(apiRows) }
    var validationMessage by remember(row.id) { mutableStateOf("") }
    var validationCell by remember(row.id) { mutableStateOf<String?>(null) }
    val subjectConfigs = remember(row.id, row.subjectConfigs) { row.subjectConfigs.toList() }
    val reviewValidation = remember(students, row.id, subjectConfigs, subjectOptions) {
        buildReviewValidation(students, row, subjectConfigs, subjectOptions)
    }
    fun save(status: String) {
        onSave(status, students.map { it.toDraft(row, subjectConfigs) })
    }
    fun updateStudentField(roll: String, field: String, value: String) {
        val config = row.subjectConfigs[field]
        val maxMarks = if (field == "total") row.maxMarks ?: subjectConfigs.mapNotNull { it.second.configuredTotal() }.sum().takeIf { it > 0 } else config.configuredTotal()
        val marksPart = if (field == "total") value else value.split("/").getOrNull(1)
        val enteredMarks = marksPart?.trim()?.toDoubleOrNull()
        if (maxMarks != null && enteredMarks != null && enteredMarks > maxMarks) {
            val label = subjectOptions[field].orEmpty().ifBlank { field }
            validationMessage = "Marks cannot exceed the configured maximum of ${maxMarks.toDisplayNumber()} for $label."
            validationCell = "$roll:$field"
            return
        }
        validationMessage = ""
        if (validationCell == "$roll:$field") validationCell = null
        students = students.map { student -> if (student.roll == roll) student.withField(field, value) else student }
    }
    AppDialogScaffold(onDismiss = onDismiss, size = AppDialogSize.Wide, dialogWidthOverride = 1200.dp, maxDialogHeight = 1100.dp, viewportFraction = 0.96f, fillDialogHeight = true, isLoading = isLoading || isSaving || isLoadingImportExport, loadingTitle = when { isSaving -> "Saving exam results"; isLoadingImportExport -> "Processing exam results"; else -> "Loading results" }) {
        AppDialogHeader("Enter Results", "", onDismiss, icon = Res.drawable.edit)
        Column(Modifier.fillMaxWidth().weight(1f).padding(horizontal = 24.dp)) {
            ResultMeta(row, studentCount)
            if (isLoading) ResultBanner("Loading results from the exam-results API…")
            if (errorMessage.isNotBlank()) ResultBanner("Unable to load results: $errorMessage")
            if (subjectNameError.isNotBlank()) ResultBanner(subjectNameError, isError = true)
            if (saveErrorMessage.isNotBlank()) ResultBanner("Unable to save results: $saveErrorMessage", isError = true)
            if (importExportError.isNotBlank()) ResultBanner("Import/export failed: $importExportError")
            ResultTabs(tab) { tab = it }
            if (tab == 0) {
                EnterResults(
                    search, students, row, subjectConfigs, subjectOptions, subjectCodes,
                    validationMessage, validationCell, !isLoading && !isSaving && !isLoadingImportExport,
                    onDownload = { onDownloadTemplate(row.backendId) },
                    onImport = { importPicker.launch() },
                    onStudentChange = ::updateStudentField,
                    onSearch = { search = it }
                )
            } else {
                ReviewPublish(
                    reviewValidation,
                    Modifier.fillMaxWidth().weight(1f)
                )
            }
        }
        if (tab == 0) {
            AppDialogFooter(
                actions = listOf(
                    AppDialogFooterAction("Cancel", onDismiss, width = 120.dp),
                    AppDialogFooterAction("Save", { save("DRAFT") }, width = 150.dp),
                    AppDialogFooterAction("Review & Publish →", { tab = 1 }, AppDialogActionStyle.Primary, width = 190.dp)
                ), footerHeight = 82.dp, contentPadding = PaddingValues(horizontal = 24.dp)
            )
        } else {
            AppDialogFooter(
                actions = listOf(
                    AppDialogFooterAction("← Back to Enter Results", { tab = 0 }, width = 200.dp),
                    AppDialogFooterAction("Cancel", onDismiss, width = 120.dp),
                    AppDialogFooterAction("Save & Publish", { if (reviewValidation.issues.isEmpty()) save("PUBLISHED") }, AppDialogActionStyle.Primary, enabled = reviewValidation.issues.isEmpty() && !isSaving, width = 170.dp)
                ), footerHeight = 82.dp, contentPadding = PaddingValues(horizontal = 24.dp)
            )
        }
    }
}

private fun ExamSubjectSelectionUi?.configuredTotal(): Double? = this?.totalMark?.toDoubleOrNull()
    ?: listOfNotNull(this?.internalMark?.toDoubleOrNull(), this?.mainMark?.toDoubleOrNull())
        .sum()
        .takeIf { it > 0 }

@Composable private fun ResultMeta(row: ExamRowUi, studentCount: Int) {
    Row(Modifier.fillMaxWidth().padding(vertical = 12.dp), horizontalArrangement = Arrangement.spacedBy(14.dp)) {
        Meta("Exam ID", row.id, 1f); Meta("Exam Name", row.name, 1.35f); Meta("Class", row.classes, .8f)
        Meta("Start Date", row.startDate, 1f); Meta("Students", studentCount.takeIf { it > 0 }?.toString() ?: "—", .7f); Meta("Status", row.status, .8f)
    }
}

@Composable private fun RowScope.Meta(label: String, value: String, weight: Float) {
    Column(Modifier.weight(weight).padding(horizontal = 3.dp), verticalArrangement = Arrangement.spacedBy(3.dp)) {
        Text(label, color = AppMuted, fontSize = 12.sp)
        if (label == "Status") {
            Box(Modifier.background(Color(0xFFFFF4E1), RoundedCornerShape(5.dp)).padding(horizontal = 8.dp, vertical = 4.dp)) { Text(value.ifBlank { "Draft" }, color = Color(0xFF8A5B12), fontSize = 13.sp, fontWeight = FontWeight.SemiBold) }
        } else {
            Text(value.ifBlank { "—" }, color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
        }
    }
}

@Composable private fun ResultTabs(selected: Int, onSelect: (Int) -> Unit) {
    Row(Modifier.fillMaxWidth().border(1.dp, AppBorder, RoundedCornerShape(8.dp)).padding(2.dp), verticalAlignment = Alignment.CenterVertically) {
        Tab(0, "Enter Results", selected == 0, onSelect); Tab(1, "Review & Publish", selected == 1, onSelect)
        Spacer(Modifier.weight(1f))
    }
}

@Composable private fun RowScope.Tab(index: Int, label: String, selected: Boolean, onSelect: (Int) -> Unit) {
    Row(Modifier.width(180.dp).clickable { onSelect(index) }.background(if (selected) AppPrimary else Color.White).padding(horizontal = 12.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Box(Modifier.size(22.dp).background(if (selected) Color.White else AppPrimarySoft, RoundedCornerShape(4.dp)), contentAlignment = Alignment.Center) { Text("${index + 1}", color = if (selected) AppPrimary else AppNavy, fontWeight = FontWeight.Bold, fontSize = 13.sp) }
        Text(label, color = if (selected) Color.White else AppNavy, fontWeight = FontWeight.SemiBold, fontSize = 13.sp)
    }
}

@Composable private fun EnterResults(search: String, students: List<ResultStudent>, row: ExamRowUi, subjectConfigs: List<Pair<String, ExamSubjectSelectionUi>>, subjectOptions: Map<String, String>, subjectCodes: Map<String, String>, validationMessage: String, validationCell: String?, actionsEnabled: Boolean, onDownload: () -> Unit, onImport: () -> Unit, onStudentChange: (String, String, String) -> Unit, onSearch: (String) -> Unit) {
    Column(Modifier.fillMaxWidth().padding(top = 12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            AdminListSearchField(search, onSearch, "Search student by name or roll no.", width = 390.dp, height = 44.dp)
            ResultFooterLeading(onDownload, onImport, actionsEnabled)
        }
        ResultBanner("For an absent subject, enter AB in the subject's fields")
        if (validationMessage.isNotBlank()) ResultBanner(validationMessage, isError = true)
        ResultEntryTable(students.filter { search.isBlank() || it.roll.contains(search, true) || it.name.contains(search, true) }, row, subjectConfigs, subjectOptions, subjectCodes, validationCell, onStudentChange)
        ResultBanner("Overall percentage is calculated only from subjects using Marks and excludes subjects marked AB.")
    }
}

@Composable private fun ResultBanner(message: String, isError: Boolean = false) {
    val accent = if (isError) Color(0xFFD32F2F) else AppPrimary
    val background = if (isError) Color(0xFFFFEBEE) else AppPrimarySoft.copy(alpha = .32f)
    Row(Modifier.fillMaxWidth().border(1.dp, accent.copy(alpha = .35f), RoundedCornerShape(8.dp)).background(background).padding(horizontal = 10.dp, vertical = 9.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Icon(painterResource(Res.drawable.info_circle), null, tint = accent, modifier = Modifier.size(19.dp)); Text(message, color = if (isError) accent else AppNavy, fontSize = 12.sp)
    }
}

@Composable private fun ResultEntryTable(students: List<ResultStudent>, row: ExamRowUi, subjectConfigs: List<Pair<String, ExamSubjectSelectionUi>>, subjectOptions: Map<String, String>, subjectCodes: Map<String, String>, validationCell: String?, onStudentChange: (String, String, String) -> Unit) {
    val horizontalScroll = rememberScrollState()
    val verticalScroll = rememberScrollState()
    val overallColumns = buildList {
        if (row.hasOverallGrade) add(ResultColumn("Grade", 68.dp))
        if (row.hasOverallMarks) {
            add(ResultColumn("% Marks", 84.dp))
            add(ResultColumn("Total Marks", 100.dp))
        }
        if (row.hasOverallRank) add(ResultColumn("Rank", 58.dp))
    }
    val subjectColumns = subjectConfigs.mapIndexedNotNull { index, (subjectId, config) ->
        val fields = listOfNotNull(
            0.takeIf { config.hasGrade }?.let { it to "Grade" },
            1.takeIf { config.hasMarks }?.let { it to "Marks / ${config.configuredTotal()?.toDisplayNumber() ?: "—"}" },
            2.takeIf { config.hasRank }?.let { it to "Rank" }
        )
        if (fields.isEmpty()) null else ResultSubjectColumn(
            title = subjectOptions[subjectId].orEmpty().ifBlank { "Subject name unavailable" },
            code = subjectCodes[subjectId].orEmpty(),
            key = subjectId,
            color = listOf(Color(0xFFE9F0FF), Color(0xFFEAF6E5), Color(0xFFF1EDFF), Color(0xFFFFF1E2), Color(0xFFE8F8FA))[index % 5],
            config = config,
            fields = fields,
            visibleFields = fields,
            width = when (fields.size) {
                1 -> 124.dp
                2 -> 160.dp
                // Keep Grade + Marks + Rank fields wide enough for two/three
                // digit values and a visible caret. The table already has a
                // horizontal scrollbar, so readability is more important
                // than squeezing all subject columns into the viewport.
                else -> 216.dp
            },
            value = { it.subjectValues[subjectId].orEmpty() }
        )
    }
    val notesWidth = 72.dp
    val overallWidth = overallColumns.sumOf { it.width.value.toDouble() }.dp
    val fixedWidth = 60.dp + 150.dp + overallWidth + notesWidth
    val subjectTableWidth = subjectColumns.sumOf { it.width.value.toDouble() }.dp.coerceAtLeast(1.dp)
    Column(Modifier.fillMaxWidth().height(540.dp).border(1.dp, AppBorder, RoundedCornerShape(8.dp))) {
        Row(Modifier.fillMaxWidth().weight(1f), verticalAlignment = Alignment.Top) {
            Column(Modifier.width(fixedWidth).fillMaxHeight().tableColumnGridLine()) {
                Column(Modifier.fillMaxWidth().padding(4.dp)) {
                    Row(Modifier.fillMaxWidth().height(74.dp).background(Color.White), verticalAlignment = Alignment.Top) {
                        Header("Roll No.", 60.dp, height = 74.dp)
                        Header("Student Name", 150.dp, height = 74.dp)
                        if (overallColumns.isNotEmpty()) {
                            Column(Modifier.width(overallWidth).fillMaxHeight()) {
                                Row(Modifier.fillMaxWidth().height(32.dp), verticalAlignment = Alignment.CenterVertically) {
                                    Header("Overall Result", overallWidth)
                                }
                                Row(Modifier.fillMaxWidth().weight(1f), verticalAlignment = Alignment.CenterVertically) {
                                    overallColumns.forEach { Header(it.title, it.width) }
                                }
                            }
                        }
                        Header("Notes", notesWidth, height = 74.dp)
                    }
                }
                Column(Modifier.fillMaxWidth().weight(1f).verticalScroll(verticalScroll).padding(horizontal = 4.dp)) {
                    students.forEach { student ->
                        val calculated = student.calculate(subjectConfigs)
                        Row(Modifier.height(resultRowHeight), verticalAlignment = Alignment.CenterVertically) {
                            Cell(student.roll, 60.dp); Cell(student.name, 150.dp)
                            if (row.hasOverallGrade) GradeDropdownCell(student.grade, 68.dp) { onStudentChange(student.roll, "grade", it) }
                            if (row.hasOverallMarks) { PercentageCell(calculated.percentage, 84.dp); Cell(calculated.total.toDisplayNumber(), 100.dp) }
                            if (row.hasOverallRank) EditableCell(student.rank, 58.dp, isError = validationCell == "${student.roll}:rank") { onStudentChange(student.roll, "rank", it) }
                            NotesCell(student.notes, notesWidth) { onStudentChange(student.roll, "notes", it) }
                        }
                        HorizontalDivider(color = AppBorder)
                    }
                }
            }
            Column(Modifier.weight(1f).fillMaxHeight()) {
                Row(Modifier.weight(1f).fillMaxWidth(), verticalAlignment = Alignment.Top) {
                    Column(Modifier.weight(1f).fillMaxHeight().horizontalScroll(horizontalScroll)) {
                        Column(Modifier.width(subjectTableWidth).padding(4.dp)) {
                            Row(Modifier.fillMaxWidth().height(32.dp).background(Color.White), verticalAlignment = Alignment.CenterVertically) {
                                if (subjectColumns.isNotEmpty()) Header("Subject Results", subjectTableWidth)
                            }
                            Row(Modifier.fillMaxWidth().height(42.dp).background(Color.White), verticalAlignment = Alignment.CenterVertically) {
                                subjectColumns.forEach { column -> SubjectHeader(column.code, column.title, column.visibleFields.joinToString("  |  ") { it.second }, column.width, column.color) }
                            }
                        }
                        Column(Modifier.width(subjectTableWidth).weight(1f).verticalScroll(verticalScroll).padding(horizontal = 4.dp)) {
                            students.forEach { student ->
                                Row(Modifier.height(resultRowHeight), verticalAlignment = Alignment.CenterVertically) {
                                    subjectColumns.forEach { column ->
                                        SubjectInputs(column.value(student), column.width, column.fields.size, column.visibleFields.map { it.first }.toSet(), column.visibleFields.filter { it.second == "Grade" }.map { it.first }.toSet(), isError = validationCell == "${student.roll}:${column.key}", onValueChange = { value -> onStudentChange(student.roll, column.key, value) })
                                    }
                                }
                                HorizontalDivider(color = AppBorder)
                            }
                        }
                    }
                    ExamResultsVerticalScrollbar(verticalScroll, Modifier.width(14.dp).padding(top = 78.dp).fillMaxHeight())
                }
                Row(Modifier.fillMaxWidth()) {
                    AdminListHorizontalScrollbar(horizontalScroll, Modifier.weight(1f))
                    Spacer(Modifier.width(14.dp))
                }
            }
        }
    }
}

@Composable
private fun ExamResultsVerticalScrollbar(state: androidx.compose.foundation.ScrollState, modifier: Modifier = Modifier) {
    val density = LocalDensity.current
    val minThumbPx = with(density) { 28.dp.toPx() }
    val thumbWidthPx = with(density) { 6.dp.toPx() }
    val canScroll by remember(state) { derivedStateOf { state.maxValue > 0 } }
    var dragGrabOffsetPx by remember { mutableStateOf(0f) }
    if (!canScroll) return

    Box(
        modifier = modifier.pointerInput(state) {
            detectDragGestures(
                onDragStart = { offset ->
                    val viewport = size.height.toFloat()
                    val content = viewport + state.maxValue
                    val thumbHeight = (viewport * viewport / content).coerceIn(minThumbPx, viewport)
                    val thumbTop = (viewport - thumbHeight) * state.value / state.maxValue.coerceAtLeast(1)
                    dragGrabOffsetPx = (offset.y - thumbTop).coerceIn(0f, thumbHeight)
                },
                onDrag = { change, _ ->
                    change.consume()
                    val viewport = size.height.toFloat()
                    val content = viewport + state.maxValue
                    val thumbHeight = (viewport * viewport / content).coerceIn(minThumbPx, viewport)
                    val maxTop = (viewport - thumbHeight).coerceAtLeast(1f)
                    val desiredTop = (change.position.y - dragGrabOffsetPx).coerceIn(0f, maxTop)
                    val desiredScroll = desiredTop / maxTop * state.maxValue
                    state.dispatchRawDelta(desiredScroll - state.value)
                },
                onDragEnd = { dragGrabOffsetPx = 0f },
                onDragCancel = { dragGrabOffsetPx = 0f }
            )
        }.drawWithContent {
            if (!canScroll || size.height <= 0f) return@drawWithContent
            val viewport = size.height
            val content = viewport + state.maxValue
            val thumbHeight = (viewport * viewport / content).coerceIn(minThumbPx, viewport)
            val maxTop = (viewport - thumbHeight).coerceAtLeast(0f)
            val thumbTop = if (state.maxValue == 0) 0f else maxTop * state.value / state.maxValue
            drawRoundRect(
                color = Color(0xFFF1F1F1),
                topLeft = Offset((size.width - thumbWidthPx) / 2f, 0f),
                size = Size(thumbWidthPx, viewport),
                cornerRadius = CornerRadius(thumbWidthPx / 2f)
            )
            drawRoundRect(
                color = Color(0xFF7A7A7A).copy(alpha = 0.9f),
                topLeft = Offset((size.width - thumbWidthPx) / 2f, thumbTop),
                size = Size(thumbWidthPx, thumbHeight),
                cornerRadius = CornerRadius(thumbWidthPx / 2f)
            )
        }
    )
}

private data class ResultColumn(val title: String, val width: Dp)
private data class ResultSubjectColumn(
    val title: String,
    val code: String,
    val key: String,
    val color: Color,
    val config: ExamSubjectSelectionUi?,
    val fields: List<Pair<Int, String>>,
    val visibleFields: List<Pair<Int, String>> = emptyList(),
    val width: Dp,
    val value: (ResultStudent) -> String
)

private fun Modifier.tableColumnGridLine(): Modifier = drawWithContent {
    drawContent()
    val stroke = 1.dp.toPx()
    val x = size.width - stroke / 2f
    drawLine(AppBorder, Offset(x, 0f), Offset(x, size.height), strokeWidth = stroke)
}

private fun Modifier.tableHeaderGridLines(): Modifier = drawWithContent {
    drawContent()
    val stroke = 1.dp.toPx()
    val x = size.width - stroke / 2f
    val y = size.height - stroke / 2f
    drawLine(AppBorder, Offset(x, 0f), Offset(x, size.height), strokeWidth = stroke)
    drawLine(AppBorder, Offset(0f, y), Offset(size.width, y), strokeWidth = stroke)
}

@Composable private fun RowScope.Header(text: String, width: Dp, height: Dp? = null) {
    Text(
        text = text,
        modifier = Modifier.width(width).then(height?.let { Modifier.height(it) } ?: Modifier.fillMaxHeight()).tableHeaderGridLines().padding(horizontal = 6.dp, vertical = 4.dp),
        color = AppNavy,
        fontSize = 11.sp,
        fontWeight = FontWeight.Bold,
        textAlign = TextAlign.Center,
        maxLines = 1,
        softWrap = false
    )
}
@OptIn(ExperimentalMaterial3Api::class)
@Composable private fun RowScope.SubjectHeader(code: String, subjectName: String, fields: String, width: Dp, color: Color) {
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(
            positioning = TooltipAnchorPosition.Above,
            spacingBetweenTooltipAndAnchor = 6.dp
        ),
        tooltip = { PlainTooltip { Text(subjectName) } },
        state = rememberTooltipState()
    ) {
        Column(Modifier.width(width).fillMaxHeight().background(color).tableHeaderGridLines().padding(vertical = 6.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text(code, color = AppNavy, fontSize = 11.sp, fontWeight = FontWeight.Bold)
            Text(fields, color = AppNavy, fontSize = 9.sp, textAlign = TextAlign.Center)
        }
    }
}
@Composable private fun RowScope.Cell(text: String, width: Dp, boxed: Boolean = false) { Box(Modifier.width(width).fillMaxHeight().tableColumnGridLine().padding(horizontal = 4.dp), contentAlignment = Alignment.Center) { Text(text, Modifier.then(if (boxed) Modifier.border(1.dp, AppBorder, RoundedCornerShape(5.dp)).padding(6.dp) else Modifier.padding(6.dp)), color = AppNavy, fontSize = 12.sp, textAlign = TextAlign.Center, maxLines = 2) } }
@Composable private fun RowScope.PercentageCell(value: Double, width: Dp) {
    Box(Modifier.width(width).fillMaxHeight().tableColumnGridLine().padding(horizontal = 4.dp), contentAlignment = Alignment.Center) {
        Text(
            "${value.toDisplayNumber()}%",
            modifier = Modifier.padding(6.dp),
            color = AppNavy,
            fontSize = 12.sp,
            textAlign = TextAlign.Center,
            maxLines = 1,
            softWrap = false
        )
    }
}
@Composable private fun RowScope.NotesCell(note: String, width: Dp, onNoteChange: (String) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    var draft by remember(note) { mutableStateOf(note) }
    Box(Modifier.width(width).fillMaxHeight().tableColumnGridLine().padding(horizontal = 4.dp), contentAlignment = Alignment.Center) {
        Icon(
            painterResource(Res.drawable.message),
            contentDescription = if (note.isBlank()) "Add student notes" else "Edit student notes",
            tint = if (note.isBlank()) AppNavy else AppPrimary,
            modifier = Modifier
                .size(28.dp)
                .clickable {
                    draft = note
                    expanded = true
                }
        )
        if (expanded) {
            Popup(
                popupPositionProvider = NotesPopupPositionProvider,
                onDismissRequest = { expanded = false },
                properties = PopupProperties(focusable = true, dismissOnBackPress = true, dismissOnClickOutside = true)
            ) {
                Surface(
                    modifier = Modifier.width(280.dp),
                    color = Color.White,
                    shape = RoundedCornerShape(10.dp),
                    border = BorderStroke(1.dp, AppBorder),
                    shadowElevation = 14.dp
                ) {
                    Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text("Student note", color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                        AppOutlinedTextField(
                            value = draft,
                            onValueChange = { draft = it },
                            modifier = Modifier.fillMaxWidth().height(82.dp),
                            placeholder = "Add a note",
                            singleLine = false
                        )
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
                            TextButton(onClick = { expanded = false }) { Text("Cancel", color = AppMuted, fontSize = 12.sp) }
                            TextButton(onClick = { onNoteChange(draft.trim()); expanded = false }) { Text("Save", color = AppPrimary, fontSize = 12.sp, fontWeight = FontWeight.SemiBold) }
                        }
                    }
                }
            }
        }
    }
}

private val NotesPopupPositionProvider = object : PopupPositionProvider {
    override fun calculatePosition(anchorBounds: IntRect, windowSize: IntSize, layoutDirection: LayoutDirection, popupContentSize: IntSize): IntOffset {
        val centeredX = anchorBounds.left + (anchorBounds.width - popupContentSize.width) / 2
        val aboveY = anchorBounds.top - popupContentSize.height - 8
        val belowY = anchorBounds.bottom + 8
        val x = centeredX.coerceIn(8, (windowSize.width - popupContentSize.width - 8).coerceAtLeast(8))
        val y = if (aboveY >= 8) aboveY else belowY.coerceAtMost((windowSize.height - popupContentSize.height - 8).coerceAtLeast(8))
        return IntOffset(x, y)
    }
}
private val compactNumericEntryWidth = 72.dp
private val compactGradeEntryWidth = 86.dp

@Composable private fun RowScope.EditableCell(value: String, width: Dp, enabled: Boolean = true, isError: Boolean = false, onValueChange: (String) -> Unit) {
    val controlWidth = minOf(width - 10.dp, compactNumericEntryWidth)
    Box(Modifier.width(width).fillMaxHeight().tableColumnGridLine().padding(horizontal = 5.dp), contentAlignment = Alignment.Center) {
        AppOutlinedTextField(value, onValueChange, modifier = Modifier.width(controlWidth).height(resultFieldHeight), singleLine = true, enabled = enabled, isError = isError)
    }
}
@Composable private fun RowScope.GradeDropdownCell(value: String, width: Dp, enabled: Boolean = true, onValueChange: (String) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    Box(Modifier.width(width).fillMaxHeight().tableColumnGridLine().padding(horizontal = 5.dp), contentAlignment = Alignment.Center) {
        Box(
            Modifier.width(width - 10.dp).height(resultFieldHeight)
                .border(1.dp, AppInputBorder, RoundedCornerShape(6.dp))
                .clickable(enabled = enabled) { expanded = true }
                .padding(horizontal = 9.dp),
            contentAlignment = Alignment.Center
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(value, color = if (enabled) AppNavy else AppMuted, fontSize = 13.sp, maxLines = 1)
                Icon(painterResource(Res.drawable.chevron_down), null, tint = AppNavy, modifier = Modifier.size(15.dp))
            }
            AppDropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false },
                width = width - 10.dp,
                containerColor = Color.White,
                shape = RoundedCornerShape(8.dp),
                border = BorderStroke(1.dp, AppBorder),
                shadowElevation = 12.dp
            ) {
                gradeOptions.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option, color = AppNavy, fontSize = 13.sp) },
                        onClick = { expanded = false; onValueChange(option) }
                    )
                }
            }
        }
    }
}

@Composable private fun RowScope.SubjectInputs(value: String, width: Dp, fieldCount: Int, visibleIndexes: Set<Int>, gradeIndexes: Set<Int>, isError: Boolean = false, onValueChange: (String) -> Unit) {
    val parts = value.split("/").map { it.trim() }
        .let { current -> current + List((maxOf(3, fieldCount) - current.size).coerceAtLeast(0)) { "" } }
    val fieldSpacing = 4.dp
    val contentWidth = width - 10.dp
    val visible = visibleIndexes.sorted()
    if (visible.isEmpty()) return
    val fieldWidth = ((contentWidth - (fieldSpacing * (visible.size - 1))) / visible.size).let { calculatedWidth ->
        minOf(calculatedWidth, if (visible.size == 1) compactNumericEntryWidth else compactNumericEntryWidth)
    }
    Row(Modifier.width(width).fillMaxHeight().tableColumnGridLine().padding(horizontal = 5.dp), horizontalArrangement = Arrangement.spacedBy(fieldSpacing, Alignment.CenterHorizontally), verticalAlignment = Alignment.CenterVertically) {
        visible.forEach { index ->
            val updatePart: (String) -> Unit = { updated -> onValueChange(parts.mapIndexed { partIndex, part -> if (partIndex == index) updated else part }.joinToString(" / ")) }
            if (index in gradeIndexes) {
                GradeDropdownCell(parts[index], width = minOf(fieldWidth, compactGradeEntryWidth), onValueChange = updatePart)
            } else {
                AppOutlinedTextField(parts[index], updatePart, modifier = Modifier.width(fieldWidth).height(resultFieldHeight), singleLine = true, isError = isError)
            }
        }
    }
}

private fun ResultStudent.withField(field: String, value: String) = when (field) {
    "grade" -> copy(grade = value)
    "total" -> copy(total = value)
    "rank" -> copy(rank = value)
    "notes" -> copy(notes = value)
    else -> copy(subjectValues = subjectValues + (field to value))
}

@Composable private fun ReviewPublish(validation: ReviewValidation, modifier: Modifier = Modifier) {
    LazyColumn(
        modifier = modifier,
        contentPadding = PaddingValues(top = 24.dp, bottom = 18.dp)
    ) {
        item {
            Text("Result Progress", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.Bold)
        }
        item {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                Progress("Total Students", validation.totalStudents.toString(), Res.drawable.users, AppPrimary)
                Progress("Results Entered", validation.enteredStudents.toString(), Res.drawable.document, AppPrimary)
                Progress("Pending", validation.pendingStudents.toString(), Res.drawable.hourglass, Color(0xFFB87300))
                Progress("Completion", "${validation.completion}%", Res.drawable.health_chart, AppPrimary)
            }
        }
        item {
            ResultBanner("Students or subjects marked AB are treated as recorded and are not counted as pending.")
        }
        item {
            Text("Validation Details", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.Bold)
        }
        if (validation.issues.isEmpty()) {
            item { ResultBanner("All student results are complete and ready to publish.") }
        } else {
            item {
                ResultBanner(
                    "${validation.issues.size} validation issue${if (validation.issues.size == 1) "" else "s"} must be resolved before publishing.",
                    isError = true
                )
            }
            item { ValidationTableHeader() }
            items(validation.issues) { issue ->
                ValidationTableRow(issue)
            }
        }
    }
}

@Composable private fun RowScope.Progress(title: String, value: String, icon: DrawableResource, accent: Color) {
    AppCard(Modifier.weight(1f).height(108.dp).border(1.dp, AppBorder, RoundedCornerShape(8.dp)), Color.White) {
        Row(Modifier.fillMaxSize().padding(14.dp), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            Box(Modifier.size(48.dp).background(AppPrimarySoft, RoundedCornerShape(8.dp)), contentAlignment = Alignment.Center) { Icon(painterResource(icon), null, tint = accent, modifier = Modifier.size(28.dp)) }
            Column { Text(title, color = AppMuted, fontSize = 13.sp); Text(value, color = AppNavy, fontSize = 25.sp, fontWeight = FontWeight.Bold) }
        }
    }
}

@Composable private fun ValidationTableHeader() {
    Row(Modifier.fillMaxWidth().background(AppPrimarySoft.copy(alpha = .22f)).border(1.dp, AppBorder, RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp))) {
        ValidationHeaderCell("Roll No.", .14f)
        ValidationHeaderCell("Student Name", .25f)
        ValidationHeaderCell("Subject / Result", .27f)
        ValidationHeaderCell("Issue", .34f)
    }
}

@Composable private fun ValidationTableRow(issue: ValidationIssue) {
    Row(
        Modifier.fillMaxWidth().height(54.dp).border(1.dp, AppBorder),
        verticalAlignment = Alignment.CenterVertically
    ) {
        VCell(issue.roll, .14f)
        VCell(issue.student, .25f)
        VCell(issue.subject, .27f)
        VCell(issue.issue, .34f)
    }
}

@Composable
private fun RowScope.ValidationHeaderCell(text: String, weight: Float) {
    Text(
        text,
        Modifier.weight(weight).padding(horizontal = 12.dp, vertical = 10.dp),
        color = AppNavy,
        fontSize = 13.sp,
        fontWeight = FontWeight.Bold,
        maxLines = 1
    )
}

@Composable private fun RowScope.VCell(text: String, weight: Float) { Text(text, Modifier.weight(weight).padding(12.dp), color = AppNavy, fontSize = 13.sp) }

@Composable private fun ResultFooterLeading(onDownload: () -> Unit, onImport: () -> Unit, enabled: Boolean) {
    Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) {
        ResultFooterButton("Download", Res.drawable.download, enabled, onDownload)
        ResultFooterButton("Import", Res.drawable.cloud_upload, enabled, onImport)
    }
}

@Composable private fun ResultFooterButton(label: String, icon: DrawableResource, enabled: Boolean, onClick: () -> Unit) {
    androidx.compose.material3.OutlinedButton(onClick = onClick, enabled = enabled, modifier = Modifier.width(120.dp).height(44.dp), shape = RoundedCornerShape(6.dp), border = BorderStroke(1.dp, AppPrimary), contentPadding = PaddingValues(horizontal = 8.dp)) {
        Icon(painterResource(icon), null, tint = AppPrimary, modifier = Modifier.size(16.dp)); Spacer(Modifier.width(8.dp)); Text(label, color = AppPrimary, fontSize = 13.sp, fontWeight = FontWeight.SemiBold, maxLines = 1)
    }
}
